import { FormikDropdown } from "@faire/deprecated-retailer-visitor-shared/components/Form/FormikDropdown";
import { FormikInput } from "@faire/deprecated-retailer-visitor-shared/components/Form/FormikInput";
import { FormikUSDateInput } from "@faire/deprecated-retailer-visitor-shared/components/Form/FormikUSDateInput";
import { IFormikInputIProps } from "@faire/deprecated-retailer-visitor-shared/components/Form/types";
import {
  IHelper,
  IHidden,
  IOptional,
  ITrackable,
} from "@faire/deprecated-retailer-visitor-shared/features/IDV/commonTypes";
import { Hidden } from "@faire/deprecated-retailer-visitor-shared/features/IDV/Hidden";
import { LabelOptional } from "@faire/deprecated-retailer-visitor-shared/features/IDV/LabelOptional";
import { getGlobalInputLabelAndHelper } from "@faire/deprecated-retailer-visitor-shared/features/IDV/resellerCertificateUtils";
import { getSelectedState } from "@faire/deprecated-retailer-visitor-shared/features/IDV/utils";
import {
  CERTIFICATE_NUMBER_MAX_CHARS,
  CERTIFICATE_NUMBER_MIN_CHARS,
  Errors,
  errorTypeToErrorMsg,
} from "@faire/deprecated-retailer-visitor-shared/features/IDV/validationUtils";
import {
  getResellerCertificateFormData,
  IIdOption,
} from "@faire/deprecated-retailer-visitor-shared/lib/getResellerCertificateFormData";
import { Core } from "@faire/design-tokens";
import { US_STATE } from "@faire/web/common/consts/US_STATE";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { IOption } from "@faire/web/slate-deprecated/Dropdown";
import { Tooltip } from "@faire/web/slate-deprecated/Tooltip";
import { Information } from "@faire/web/slate/icons/Information";
import { Column, Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { useField, useFormikContext } from "formik";
import React, {
  ComponentProps,
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import * as Yup from "yup";

export interface ICertificateFieldsContext {
  fields?: IIdOption[];
}

const CertificateFieldsContext = createContext<ICertificateFieldsContext>({});

export const useUSACertificateFieldsContext = () => {
  return useContext(CertificateFieldsContext);
};

export const USACertificateFieldsContextProvider: React.FC<
  PropsWithChildren
> = ({ children }) => {
  const { strictLocalize } = useStrictLocalization();
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [{ value: state }, { initialValue: initialState }] = useField("state");
  const [, { initialValue: initialExpiresAt }] = useField("expires_at");
  const [{ value: selectedField }] = useField("selectedField");
  const [, { initialValue: initialSelectedFieldValue = "" }] = useField(
    selectedField?.id ?? "_"
  );
  const [{ value: confirmedNotEligibleForCertificate }] = useField<boolean>(
    "confirmed_not_eligible_for_certificate"
  );
  const [{ value: shippingToAnotherStateWithSalesTax }] = useField(
    "shippingToAnotherStateWithSalesTax"
  );
  const [{ value: registeredState }] = useField("registered_state");

  const formData = useMemo(() => {
    // user may opt to use registered_state instead of state when their delivery address is in a state that differs
    // from the state their store is registered in.
    const selectedState = getSelectedState(
      registeredState,
      state,
      shippingToAnotherStateWithSalesTax,
      confirmedNotEligibleForCertificate
    );
    return getResellerCertificateFormData(
      selectedState ? selectedState : state,
      strictLocalize
    );
  }, [
    state,
    strictLocalize,
    shippingToAnotherStateWithSalesTax,
    registeredState,
    confirmedNotEligibleForCertificate,
  ]);
  const idOptions = formData?.idOptions;
  const isMultiFields = (idOptions?.length ?? 0) > 1;
  const isExpirationRequired =
    formData?.expirationDateRequired && !confirmedNotEligibleForCertificate;
  const taxAccountIdNumber = formData?.taxAccountIdNumber;
  const isInitialState = state === initialState;

  const selectedFieldId = selectedField?.id;

  const context = useMemo((): ICertificateFieldsContext => {
    return { fields: idOptions };
  }, [idOptions]);

  useEffect(() => {
    setFieldValue("documentTypeId", "", false);
    setFieldValue(
      "selectedField",
      isMultiFields ? undefined : idOptions?.[0],
      false
    );
  }, [idOptions, isMultiFields, setFieldTouched, setFieldValue]);

  // Apparently Formik doesn't handle well manual field reset and validations
  // Each setFieldValue has it's own form context and they are async, leading to
  // wrong validations and field values. The double `then` ensures everything is
  // validated and set correctly.
  useEffect(() => {
    Promise.all(
      selectedFieldId
        ? [
            setFieldValue(
              selectedFieldId,
              isInitialState ? initialSelectedFieldValue : "",
              false
            ),
            setFieldTouched(selectedFieldId, false, false),
            setFieldValue(
              "expires_at",
              isInitialState ? initialExpiresAt : "",
              false
            ),
            setFieldTouched("expires_at", false, false),
          ]
        : []
    )
      .then(() =>
        Promise.all([
          setFieldValue("isMultiFields", isMultiFields, false),
          setFieldValue(
            "isTaxAccountIdNumberRequired",
            taxAccountIdNumber,
            false
          ),
        ])
      )
      .then(() =>
        setFieldValue("isExpirationRequired", isExpirationRequired, true)
      );
  }, [
    formData,
    initialSelectedFieldValue,
    isExpirationRequired,
    isMultiFields,
    isInitialState,
    initialExpiresAt,
    selectedFieldId,
    setFieldTouched,
    setFieldValue,
    taxAccountIdNumber,
  ]);

  return (
    <CertificateFieldsContext.Provider value={context}>
      {children}
    </CertificateFieldsContext.Provider>
  );
};

export const USAStateDropdown: React.FC<
  Omit<ComponentProps<typeof FormikDropdown>, "options">
> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const options = useMemo(() => US_STATE(strictLocalize), [strictLocalize]);
  return <FormikDropdown options={options} {...props} />;
};

export const USAStateSelectorField: React.FC<
  {
    tooltip?: string;
    disabled?: boolean;
  } & ITrackable &
    IHelper
> = ({ tooltip, disabled, trackChange, helper }) => {
  const [, { error, touched }] = useField("state");

  return (
    <Column>
      <Row gap="1/2x">
        <Typography
          color={touched && error ? Core.text.critical : Core.text.default}
        >
          <StrictLocalMsg
            id="I+GBBa"
            defaultMessage="State"
            description={{ text: "A label to denote the geographic state" }}
          />
        </Typography>
        {tooltip ? (
          <Tooltip
            targetNode={
              <Information fontSize="18px" color={Core.icon.default} />
            }
            width={250}
            position="right"
            data-test-id="state-label-tooltip"
            contents={[{ body: tooltip }]}
          />
        ) : null}
      </Row>
      <USAStateDropdown
        name="state"
        data-test-id="rc-form-state-selector"
        disabled={disabled}
        trackChange={trackChange}
        helper={helper}
      />
    </Column>
  );
};

export const USADocumentTypeField: React.FC<
  IOptional &
    IHidden &
    IHelper & { trackChange?: (name: string, value: string) => void }
> = ({ optional, hidden, helper, trackChange }) => {
  const [, , { setValue: setSelectedField }] = useField("selectedField");
  const { fields } = useUSACertificateFieldsContext();

  const options = useMemo(() => {
    if (!fields?.length) {
      return [];
    }

    return fields.map((field) => ({
      label: field.label,
      value: field.id as IIdOption["id"],
    }));
  }, [fields]);

  const handleSelect = useCallback(
    (option: IOption<IIdOption["id"]>) => {
      const selectedField = fields?.find((field) => field.id === option.value);
      setSelectedField(selectedField);
    },
    [fields, setSelectedField]
  );

  return (
    <Hidden hidden={!fields?.length || hidden}>
      <Typography>
        <LabelOptional optional={optional}>
          <StrictLocalMsg
            id="E07ih2"
            defaultMessage="Document type"
            description={{
              text: "A label to denote the type of document ID that will be used",
              img: "https://cdn.faire.com/fastly/f49fdb64d66e2aa2af6249cbf7dbc12c6cb1d74b58ae9e520069992933b8aeff.png",
            }}
          />
        </LabelOptional>
      </Typography>
      <FormikDropdown
        name="documentTypeId"
        data-test-id="rc-form-id-type-selector"
        options={options}
        onSelect={handleSelect}
        helper={helper}
        trackChange={trackChange}
      />
    </Hidden>
  );
};

export const USACertificateNumberField: React.FC<
  IOptional & ITrackable & IHelper & Pick<IFormikInputIProps, "tooltip">
> = ({ optional, trackChange, helper, tooltip }) => {
  const [, , { setValue: setExpirationValue }] = useField("expires_at");
  const [{ value: selectedField }] = useField<IIdOption | undefined>(
    "selectedField"
  );
  const { strictLocalize } = useStrictLocalization();

  const handleChange = (name: string, value: string) => {
    trackChange?.(name, value);
    if (!value) {
      setExpirationValue(undefined);
    }
  };

  const { label, placeholder, id } = selectedField ?? {};

  return (
    <Hidden hidden={!selectedField}>
      <FormikInput
        data-test-id="rc-form-cert-number-input"
        key={id ?? "_"}
        name={id ?? "_"}
        tooltip={tooltip}
        label={<LabelOptional optional={optional}>{label}</LabelOptional>}
        placeholder={placeholder}
        helper={
          helper ??
          strictLocalize({
            id: "FJnLn8",
            defaultMessage:
              "Find this on your reseller’s permit or the license you got from the state.",
            description: {
              text: "Helper text explaining where to find the information required (reseller permit, license number, etc)",
              img: "https://cdn.faire.com/fastly/f49fdb64d66e2aa2af6249cbf7dbc12c6cb1d74b58ae9e520069992933b8aeff.png",
            },
          })
        }
        trackChange={handleChange}
      />
    </Hidden>
  );
};

export const getGenericCertitifateSchema = (
  schema: Yup.StringSchema,
  strictLocalize: StrictLocalizeFunction,
  optional: boolean | undefined,
  certificateName = ""
) => {
  const invalidFormatMsg = getGenericCertificateErrorMsg(
    strictLocalize,
    certificateName
  );

  schema = schema
    .min(CERTIFICATE_NUMBER_MIN_CHARS, invalidFormatMsg)
    .max(CERTIFICATE_NUMBER_MAX_CHARS, invalidFormatMsg);

  if (!optional) {
    schema = schema.required(
      errorTypeToErrorMsg(strictLocalize).get(Errors.IS_EMPTY)
    );
  }

  return schema;
};

export const getGenericCertificateErrorMsg = (
  strictLocalize: StrictLocalizeFunction,
  certificateName = ""
) => {
  return strictLocalize(
    {
      id: "KaHdcA",
      defaultMessage: "This isn't a valid {certificateFullyTranslatedSentence}",
      description: {
        text: "Indicate that the format of the certificate (or permit, or any other document) is invalid",
      },
    },
    { certificateFullyTranslatedSentence: certificateName }
  );
};

export const USATaxAccountIdField: React.FC<
  IOptional & ITrackable & IHidden
> = ({ optional, trackChange, hidden }) => {
  const [{ value: isTaxAccountIdNumberRequired }] = useField(
    "isTaxAccountIdNumberRequired"
  );
  const { strictLocalize } = useStrictLocalization();
  const helper = strictLocalize({
    id: "4EQVnh",
    defaultMessage:
      "This is the “Sales and Use Tax Account ID Number” provided by your state.",
    description: {
      text: "State here refers to a geographic state",
    },
  });

  if (!isTaxAccountIdNumberRequired || hidden) {
    // not rendering this field instead of hiding because it uses the same request field certificate_number
    // and rendering it would cause an issue with Formik
    return null;
  }

  return (
    <FormikInput
      data-test-id="rc-form-cert-number-input"
      name="certificate_number"
      label={
        <LabelOptional optional={optional}>
          <StrictLocalMsg id="BDwpoB" defaultMessage="Tax Account ID Number" />
        </LabelOptional>
      }
      placeholder="351234123412"
      trackChange={trackChange}
      helper={helper}
    />
  );
};

export const USAExpirationDateField: React.FC<
  IOptional & ITrackable & IHidden & { label?: React.ReactNode }
> = ({ optional, trackChange, hidden, label }) => {
  const { strictLocalize } = useStrictLocalization();
  const optionalLabel = strictLocalize({
    id: "YqF+Bo",
    defaultMessage: "(if applicable)",
    description: { text: "as in not required if not applicable" },
  });
  return (
    <Hidden hidden={hidden}>
      <FormikUSDateInput
        name="expires_at"
        trackChange={trackChange}
        label={
          label ?? (
            <LabelOptional optional={optional} optionalLabel={optionalLabel}>
              <StrictLocalMsg
                id="CTesr2"
                defaultMessage="Expiration date"
                description={{
                  text: "Label for the expiration date input field",
                }}
              />
            </LabelOptional>
          )
        }
      />
    </Hidden>
  );
};

export const GlobalCertificateNumberField: React.FC<
  {
    country: string | undefined;
  } & IOptional &
    ITrackable
> = ({ trackChange, country, optional }) => {
  const { strictLocalize } = useStrictLocalization();
  const { label, helper } = getGlobalInputLabelAndHelper(
    country,
    strictLocalize
  );

  return (
    <FormikInput
      name="business_identifier.identifier_value"
      label={<LabelOptional optional={optional}>{label}</LabelOptional>}
      helper={helper}
      trackChange={trackChange}
    />
  );
};

export const LegalBusinessNameField: React.FC<IOptional & ITrackable> = ({
  trackChange,
  optional,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const label = strictLocalize({
    id: "om1Y9J",
    defaultMessage: "Legal business name",
    description: {
      text: "Label for the legal business name field",
      img: "https://cdn.faire.com/fastly/a9a314630746fdc15a7a272dfcada016ad06fccbaeaeb090b665b1460571aa81.png",
    },
  });

  return (
    <FormikInput
      name="legal_business_name"
      autoComplete="legal-business-name"
      label={<LabelOptional optional={optional}>{label}</LabelOptional>}
      data-test-id="legal-business-name"
      trackChange={trackChange}
    />
  );
};
